import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { styled } from '@this/constants/themes';
import { ByMedia } from '@this/shared/ui/other/by_media';
import HeaderContentSm from './header_content_sm';
import { ItemIcon, ReportIcon, ApprovalIcon, SettingIcon } from './header_icons';
import type User2 from '../../../domain/user/user2';

export type ExpensesHeaderUser = Pick<User2, 'id' | 'name' | 'email'> & {
  isOrganizationAdmin?: boolean;
  is_expenses_approver?: boolean;
};

type ExpensesHeaderOrganization = Pick<NonNullable<User2['organization']>, 'name'>;

interface Props {
  user: ExpensesHeaderUser | null | undefined;
  organization?: ExpensesHeaderOrganization | null;
}

const wordBreakPoint = 11;

const truncate = (str: string) => (str.length > wordBreakPoint ? `${str.slice(0, wordBreakPoint)}...` : str);

const getOrganizationName = (organization?: ExpensesHeaderOrganization | null) => {
  if (!organization?.name) return '';

  return truncate(organization.name);
};

const ExpensesHeader: React.FC<Props> = ({ user, organization }) => {
  const location = useLocation();
  const [path, setPath] = useState(location.pathname);
  const history = useHistory();
  const [showAccount, setShowAccount] = useState(false);
  const accountRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setShowAccount(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return history.listen(_location => {
      setPath(_location.pathname);
    });
  }, [history]);

  const icon = (_path: string, iconType: 'item' | 'report' | 'approval' | 'setting') => {
    const isActive = path.startsWith(_path);
    switch (iconType) {
      case 'item':
        return <ItemIcon isActive={isActive} />;
      case 'report':
        return <ReportIcon isActive={isActive} />;
      case 'approval':
        return <ApprovalIcon isActive={isActive} />;
      case 'setting':
        return <SettingIcon isActive={isActive} />;
      default:
        return null;
    }
  };
  const title = (_path: string, label: string) => {
    const className = path.startsWith(_path) ? 'active' : '';
    return <span className={className}>{label}</span>;
  };

  return (
    <Header>
      <HeaderWrapper>
        <HeaderContent>
          <HeaderLogoWrapper>
            <HeaderLogo href="/aiexpense/report_items">
              <img src="/images/expenses/AIE_572px.png" height="19" alt="AI EXPENSE" />
            </HeaderLogo>
          </HeaderLogoWrapper>
          <HeaderLinkArea>
            {user && (
              <ByMedia>
                {matches =>
                  matches.sm ? (
                    <HeaderContentSm user={user} />
                  ) : (
                    <>
                      <HeaderLink>
                        <Link to="/aiexpense/report_items">
                          {icon('/aiexpense/report_items', 'item')}
                          {title('/aiexpense/report_items', '登録')}
                        </Link>
                      </HeaderLink>
                      <HeaderLink>
                        <Link to="/aiexpense/reports">
                          {icon('/aiexpense/reports', 'report')}
                          {title('/aiexpense/reports', '申請')}
                        </Link>
                      </HeaderLink>
                      {(user?.isOrganizationAdmin || user?.is_expenses_approver) && (
                        <HeaderLink>
                          <Link to="/aiexpense/approvals">
                            {icon('/aiexpense/approvals', 'approval')}
                            {title('/aiexpense/approvals', '承認')}
                          </Link>
                        </HeaderLink>
                      )}
                      {user?.isOrganizationAdmin && (
                        <HeaderLink>
                          <Link to="/aiexpense/organization">
                            {icon('/aiexpense/organization', 'setting')}
                            {title('/aiexpense/organization', '管理')}
                          </Link>
                        </HeaderLink>
                      )}
                    </>
                  )
                }
              </ByMedia>
            )}
            {user && (
              <AccountMenu ref={accountRef}>
                <AccountDivider />
                <NameArea onClick={() => setShowAccount(!showAccount)}>
                  <div>
                    <span data-wovn-ignore>{getOrganizationName(organization)}</span>
                    <br />
                    <span data-wovn-ignore>{truncate(user.name)}</span>
                  </div>
                  <Triangle>▼</Triangle>
                </NameArea>
                {showAccount && (
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: '50px',
                      background: 'white',
                      boxShadow: '0 0 20px rgba(0,0,0,0.2)',
                      borderRadius: '0 0 12px 12px',
                      width: '220px',
                      padding: '20px',
                      textAlign: 'center',
                      zIndex: 9999
                    }}
                  >
                    <div style={{ fontSize: '20px', fontWeight: 'bold', margin: '7px 0' }} data-wovn-ignore>
                      {user.name}
                    </div>
                    <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#555' }} data-wovn-ignore>
                      {user.email}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        marginTop: '15px',
                        fontSize: '11px'
                      }}
                    >
                      <a href="/aiexpense/account/edit" style={{ color: 'inherit' }}>
                        アカウント設定
                      </a>
                      <div style={{ width: '2px', height: '8px', background: '#ccc' }} />
                      <a href="/aiexpense/users/sign_out" style={{ color: 'inherit' }}>
                        ログアウト
                      </a>
                    </div>
                  </div>
                )}
              </AccountMenu>
            )}
          </HeaderLinkArea>
        </HeaderContent>
      </HeaderWrapper>
    </Header>
  );
};

export const headerTxtColor = '#efedea';

export const headerHeight = '50px';

const contentPadding = '16px';

const Header = styled.header`
  height: ${headerHeight};
`;

const HeaderWrapper = styled.div`
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.05);
  background-color: black;
  height: ${headerHeight};
  box-sizing: border-box;
  @media screen and (max-width: 767px) {
    padding: unset;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  margin: 0 10px;
  @media screen and (max-width: 767px) {
    margin: 0;
  }
`;

const HeaderLogoWrapper = styled.h1`
  margin: 0;
  @media screen and (max-width: 767px) {
    padding: 16px 0;
  }
`;

const HeaderLogo = styled.a`
  display: flex;
  z-index: 9999;
  img {
    height: calc(${headerHeight} - (${contentPadding} * 2));
  }
`;

const HeaderLinkArea = styled.ul`
  display: flex;
  align-items: center;
  height: 100%;
`;

const AccountMenu = styled.li`
  position: relative;
  list-style: none;
  display: flex;
  align-items: center;
  @media screen and (max-width: 767px) {
    display: none;
  }
`;

const AccountDivider = styled.div`
  width: 2px;
  height: 20px;
  background: ${headerTxtColor};
  margin: auto 0;
`;

const NameArea = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: ${headerHeight};
  color: ${headerTxtColor};
  font-size: 13px;
  line-height: 15px;
  padding: 8px 8px 8px 16px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const Triangle = styled.div`
  font-size: 7px;
  margin-left: 8px;
`;

export const HeaderLink = styled.li`
  margin-right: 24px;

  &:last-child {
    margin-right: 0;
  }

  a {
    font-size: 16px;
    display: flex;
    align-items: center;
    height: 100%;
    line-height: 100%;
    color: ${headerTxtColor};
    font-weight: bold;
  }

  img,
  svg {
    display: inline-block;
    height: 15px;
    margin-right: 6px;
  }

  span {
    display: inline-block;

    &.active {
      color: #53c4f1;
    }
  }

  @media screen and (max-width: 767px) {
    margin-right: 0;

    a {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      padding: 20px 0;
      white-space: nowrap;
    }
  }
`;

export default ExpensesHeader;
